import React, { useState, useEffect } from 'react';
import { X, Tag } from 'lucide-react';
import { Card, CardContent } from './Card';
import { Button } from './Button';
import { ImageUpload } from './ImageUpload';
import { useToast } from './Toast';
import { productsService } from '../services/firebaseService';

/**
 * Modal pour gérer l'image et le badge d'un produit
 */
export const ProductVisualModal = ({ product, isOpen, onClose, onSave }) => {
  const toast = useToast();
  const [imageUrl, setImageUrl] = useState(product?.image_url || '');
  const [badge, setBadge] = useState(product?.badge || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setImageUrl(product.image_url || '');
      setBadge(product.badge || '');
    }
  }, [product]);

  const badges = [
    { value: '', label: 'Aucun badge', color: 'bg-gray-200' },
    { value: 'bestseller', label: 'Best Seller', color: 'bg-yellow-400' },
    { value: 'nouveau', label: 'Nouveau', color: 'bg-blue-500' },
    { value: 'promo', label: 'Promo', color: 'bg-red-500' }
  ];

  const handleSave = async () => {
    setSaving(true);
    try {
      // FIREBASE DIRECT (plus de backend Python)
      await productsService.update(product.id, {
        image_url: imageUrl,
        badge: badge || null
      });

      toast.success('Image et badge mis à jour');
      onSave({ ...product, image_url: imageUrl, badge: badge || null });
      onClose();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur', 'Impossible de sauvegarder les modifications');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Image & Badge - {product?.name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <CardContent className="p-6 space-y-6">
          {/* Upload d'image avec recadrage et suppression de fond */}
          <ImageUpload
            currentImage={imageUrl}
            onImageChange={setImageUrl}
            label="Image du produit"
            folder="products"
            showRemoveBackground={true}
            showCrop={true}
            aspectRatio={1}
          />

          {/* Sélection du badge */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-3">
              <Tag className="w-4 h-4 inline mr-2" />
              Badge du produit
            </label>
            <div className="grid grid-cols-2 gap-3">
              {badges.map((b) => (
                <button
                  key={b.value}
                  type="button"
                  onClick={() => setBadge(b.value)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    badge === b.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`${b.color} text-white px-3 py-1 rounded-full text-sm font-bold mb-2 inline-block`}>
                    {b.label}
                  </div>
                  <p className="text-xs text-gray-600">{b.value || 'Aucun'}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1"
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
